// @flow
import React, {useEffect, useState, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';
import queryString from 'query-string';
import {
  Divider,
  Grid,
  Hidden,
  makeStyles,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {CheckCircle, RadioButtonUnchecked} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {classNames} from '../../../../utils/react/className';

// import {useIsFeatureInTier} from '../../../../utils/component';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {usePage} from '../../../page/utils';

import horizontalShape from '../../../../assets/img/horizontal-shape.png';
import verticalShape from '../../../../assets/img/vertical-shape.png';

import Collapsible from '../../../../elements/Collapsible/Collapsible';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import AboutUsSection from './FormSections/AboutUsSection';
import CoverSection from './FormSections/CoverSection';
import TemplateSection from './FormSections/TemplateSection';
import ColorsSection from './FormSections/ColorsSection';
import FontsSection from './FormSections/FontsSection';

import ProplusUpgradeBanner from '../../../../elements/ProplusUpgradeBanner/ProplusUpgradeBanner';
import customStoreImgMobile from '../../../../assets/img/downgrade/customstore-mobile.webp';
import customStoreImgDesktop from '../../../../assets/img/downgrade/customstore-desktop.webp';

import {scrollTop} from '../../../../utils/dom';
import {DEFAULT_COLOR_OPTIONS} from '../api';
import {FONTS_OPTIONS} from './FormSections/FontsSection';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import {getImageUrl} from '../../../../utils/image_provider';
// import {usePrevious} from '../../../../utils/react';
import {useIsFeatureInTier} from '../../../../utils/component';
import PremiumFeatureBadge from '../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';

import type {SaveInformationRequest} from '../api';
import type {Store, StoreInformation} from '../../../../entities';
import RestrictionModal from './RestricionModal/RestrictionModal';
import FaviconSection from './FormSections/FaviconSection';
import BackgroundSection from './FormSections/BackgroundSection';
import {useStoreMetadata, useStoreBusinessModel} from '../../../store/utils';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import Tag from '../../../../elements/Tag/Tag';

type Props = {
  store?: Store,
  storeInformation?: StoreInformation,
  isSaving?: boolean,
  error?: string,
  saveStoreInformation: ({
    storeInformationRequest: SaveInformationRequest,
  }) => void,
  saveStoreInformationSuccess?: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
  },
  cardWhite: {
    padding: theme.spacing(2, 3),
    background: '#FFFFFF',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&[data-disabled=true]': {
      pointerEvents: 'none',
    },
  },
  titleFormText: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: 16,
    [theme.breakpoints.up('md')]: {
      fontSize: 14,
    },
  },
  subtitleFormText: {
    marginTop: theme.spacing(1),
    color: colors.grey700,
    fontSize: 14,
  },
  divider: {
    margin: theme.spacing(1, 0, 2, 0),
    backgroundColor: '#E8E8ED',
  },
  collapsible: {
    marginTop: '0!important',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  switch: {
    marginBlock: 0,
  },
  buttonSquare: {
    width: '100%',
    height: '100%',
    padding: 0,
    '& span': {
      height: '100%',
    },
  },
  skeletonSquare: {
    width: '100%',
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.grey200,
    borderRadius: '8px',
    padding: theme.spacing(4, 0),
    flexDirection: 'column',
    gap: '.5rem',
  },
  containerCheckedBox: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  bottomBar: {
    zIndex: 1200,
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    borderTop: '1px solid transparent',
    transition: 'border-color 0.2s ease',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
  },
  bottomBarWithChanges: {
    borderTop: `1px solid ${colors.grey400}`,
  },
  bottomBarContent: {
    maxWidth: 1300,
    padding: theme.spacing(2, 4),
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  mobileBottomButton: {
    zIndex: 1,
    position: 'fixed',
    bottom: 64,
    left: 0,
    height: 48,
    margin: theme.spacing(2),
    width: 'calc(100% - 32px)',
    fontSize: '1.125rem',
  },
  onboardingMessage: {
    padding: '.75rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderRadius: '8px',
  },
  onboardingMessageText: {
    color: colors.grey700,
    fontSize: 14,
    lineHeight: 1.6,
  },
  bottomBarText: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    color: colors.grey700,
  },
  unsavedChangesIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  unsavedChangesDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: colors.primary,
  },
  unsavedChangesText: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.grey700,
  },
  bottomBarButton: {
    height: 36,
    fontSize: 16,
  },
  upgradeModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  upgradeContainer: {
    position: 'absolute',
    top: 72,
    right: 0,
    zIndex: 100,
    width: '100%',
    height: '125vh',

    [theme.breakpoints.up('md')]: {
      height: 'calc(100vh - 72px)',
    },
  },
  twoLinesTitle: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  rowTitle: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  descriptionText: {
    fontSize: '0.875rem',
    color: colors.grey800,
    marginBottom: theme.spacing(2),
  },
  descriptionBold: {
    fontWeight: 'bold',
  },
  rowEnableSetting: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  helperText: {
    fontSize: 14,
    color: colors.grey900,
    fontWeight: 500,
  },
}));

const bannerData = {
  title: 'Personaliza tu tienda',
  benefits: [
    'Elige la plantilla de tu preferencia.',
    'Selecciona los colores de tu tienda.',
    'Personaliza tipográfica y color de letra.',
    'Escoge la forma de la foto de tus productos entre cuadrado y vertical.',
  ],
  content: {
    title: 'Personaliza tu tienda',
    image: {
      mobile: customStoreImgMobile,
      desktop: customStoreImgDesktop,
    },
  },
  contentBg: '#18003D',
};

export default usePage<Props>(Customize, 'Customize Store');

function Customize(props: Props) {
  const {
    // store,
    storeInformation,
    saveStoreInformation,
    isSaving,
    error,
    saveStoreInformationSuccess,
  } = props;

  const mobile = useMediaQuery('(max-width:600px)');
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();
  const queryObject = queryString.parse(location.search);

  const {register, errors, handleSubmit, setValue, watch} = useForm();

  const [productShape, setProductShape] = useState('SQUARE');
  const [templateType, setTemplateType] = useState('TEMPLATE_1');
  const [fontsSelected, setFontsSelected] = useState(4);
  const [logoShape, setLogoShape] = useState('SQUARE');

  const [shoppingCardEnabled, setShoppingCardEnabled] = useState(true);

  const [descriptionShow, setDescriptionShow] = useState(false);
  const [coverShow, setCoverShow] = useState(false);

  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [logoImageFile, setLogoImageFile] = useState<File | null>(null);

  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);

  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  const [colorSettingOption, setColorSettingOption] = useState('0');
  const [colorOptionSelected, setColorOptionSelected] = useState(1);

  const [primaryFontColor, setPrimaryFontColor] = useState('#FFFFFF');
  const [secondaryFontColor, setSecondaryFontColor] = useState('#3A3A49');

  const [isValidUrl, setIsValidUrl] = useState(true);
  const [videoSocialMediaTypeId, setVideoSocialMediaTypeId] = useState();

  const [upgradeModal, setUpgradeModal] = useState(false);
  const [missingLogo, setMissingLogo] = useState(false);
  const [missingCover, setMissingCover] = useState(false);
  const [missingFontColor, setMissingFontColor] = useState(false);

  const [navigationButtonShow, setNavigationButtonShow] = useState(false);
  const [navigationButtonText, setNavigationButtonText] = useState('');
  const [navigationButtonUrl, setNavigationButtonUrl] = useState('');

  const [contactInformationEnabled, setContactInformationEnabled] = useState(
    true
  );

  const [faviconFile, setFaviconFile] = useState<null | File>(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const businessModel = useStoreBusinessModel();

  const storeElementTypeName = useMemo(() => {
    if (businessModel === 'SERVICE') {
      return 'servicio';
    }

    return 'producto';
  }, [businessModel]);

  const closeUpgradeModal = () => {
    setUpgradeModal(false);
  };
  // const openUpgradeModal = () => {
  //   setUpgradeModal(true);
  // };

  const storeMetadata = useStoreMetadata();

  const hasStoreFavicon = useIsFeatureInTier('STORE_FAVICON');

  const hasCustomTemplateFeature = useIsFeatureInTier('STOREFRONT_TEMPLATES');
  const hasCustomFontFeature = useIsFeatureInTier('FONT_CONFIGURATION');
  const hasImageShapeFeature = useIsFeatureInTier('IMAGE_CONFIGURATION');
  const hasAdvancedColorSetting = useIsFeatureInTier(
    'ADVANCED_COLOR_CONFIGURATION'
  );

  const hasColorFeature = useIsFeatureInTier('COLOR_CONFIGURATION');
  const hasStoreInformationFeature = useIsFeatureInTier('STORE_INFORMATION');
  const hasCoverPhotoFeature = useIsFeatureInTier('COVER_PHOTO');
  const hasAboutUsFeature = useIsFeatureInTier('STORE_PROFILE_INFORMATION');

  const hasCustomizeStoreFeatureDisabled =
    !hasCustomTemplateFeature &&
    !hasCustomFontFeature &&
    !hasColorFeature &&
    !hasImageShapeFeature &&
    !hasStoreInformationFeature &&
    !hasCoverPhotoFeature &&
    !hasAboutUsFeature;

  // const handleClickCustomTemplate = () => {
  //   if (!hasCustomTemplateFeature) openUpgradeModal();
  // };
  // const handleClickCustomFontFeature = () => {
  //   if (!hasCustomFontFeature) openUpgradeModal();
  // };

  // const handleClickImageShapeFeature = () => {
  //   if (!hasImageShapeFeature) openUpgradeModal();
  // };

  // const prevLogoShape = usePrevious(logoShape);

  useEffect(() => {
    if (storeInformation) {
      setContactInformationEnabled(
        storeInformation.storeContactInformationEnabled
      );

      if (storeInformation.colorSetting) {
        const cSetting = storeInformation.colorSetting;
        const cOption = DEFAULT_COLOR_OPTIONS.findIndex(
          op =>
            op.primaryBackgroundColor === cSetting.primaryBackgroundColor &&
            op.secondaryBackgroundColor === cSetting.secondaryBackgroundColor
        );
        //Temporary for downgrade
        if (!hasAdvancedColorSetting) {
          if (cOption > -1) {
            setColorOptionSelected(cOption);
          } else setColorOptionSelected(1);
        } else if (cSetting.enabledAdvancedConfiguration)
          setColorSettingOption('1');
        else setColorOptionSelected(cOption);

        setPrimaryFontColor(cSetting.primaryFontColor);
        setSecondaryFontColor(cSetting.secondaryFontColor);
      }
      if (
        hasCustomFontFeature &&
        storeInformation.fontSetting &&
        storeInformation.fontSetting.primaryFontType &&
        storeInformation.fontSetting.secondaryFontType
      ) {
        const pFontType = storeInformation.fontSetting.primaryFontType;
        const sFontType = storeInformation.fontSetting.secondaryFontType;
        const fOption = FONTS_OPTIONS.findIndex(
          fp =>
            fp.primaryFontTypeId === pFontType.id &&
            fp.secondaryFontTypeId === sFontType.id
        );
        if (fOption > -1) setFontsSelected(fOption);
      }
      if (
        hasImageShapeFeature &&
        storeInformation.imageSetting &&
        storeInformation.imageSetting.productImageShapeType
      ) {
        setProductShape(storeInformation.imageSetting.productImageShapeType);
      }
      if (
        hasCustomTemplateFeature &&
        storeInformation.storefrontTemplateSetting &&
        storeInformation.storefrontTemplateSetting.storefrontTemplateType
      ) {
        setTemplateType(
          storeInformation.storefrontTemplateSetting.storefrontTemplateType
        );
      }
      if (storeInformation.logoShapeType)
        setLogoShape(storeInformation.logoShapeType);

      if (storeInformation.shoppingCartDisabled) {
        setShoppingCardEnabled(!storeInformation.shoppingCartDisabled);
      }
    }
  }, [
    hasAdvancedColorSetting,
    hasCustomFontFeature,
    hasCustomTemplateFeature,
    hasImageShapeFeature,
    storeInformation,
  ]);

  useEffect(() => {
    if (storeInformation) {
      if (storeInformation.logoId) {
        setLogoImage(getImageUrl(storeInformation.logoId));
      }
      if (storeInformation.coverId) {
        setCoverImage(getImageUrl(storeInformation.coverId));
      }
      if (storeInformation.profileInformation) {
        if (storeInformation.profileInformation.videoUrl) {
          setValue('videoUrl', storeInformation.profileInformation.videoUrl);
        }
        if (storeInformation.profileInformation.profilePhotoId) {
          setProfileImage(
            getImageUrl(storeInformation.profileInformation.profilePhotoId)
          );
        }
      }
    }
  }, [setValue, storeInformation]);

  useEffect(() => {
    if (storeInformation) {
      setDescriptionShow(storeInformation.enabledDescription);
      setCoverShow(storeInformation.enabledCover);
      setNavigationButtonShow(storeInformation.storeNavButtonEnabled);
      setNavigationButtonText(storeInformation.storeNavButtonText);
      setNavigationButtonUrl(storeInformation.storeNavButtonUrl);
    }
  }, [storeInformation]);

  useEffect(() => {
    if (colorSettingOption === '1') {
      if (storeInformation && storeInformation.colorSetting) {
        const cSetting = storeInformation.colorSetting;
        setValue('primaryBackgroundColor', cSetting.primaryBackgroundColor);
        setValue('secondaryBackgroundColor', cSetting.secondaryBackgroundColor);
      }
    }
  }, [colorSettingOption, setValue, storeInformation]);

  useEffect(() => {
    if (saveStoreInformationSuccess && queryObject.dashboard) {
      history.push('/');
    }
  }, [history, queryObject, saveStoreInformationSuccess]);

  useEffect(() => {
    if (saveStoreInformationSuccess && queryObject.fromOnboarding) {
      history.push('/');
    }
  }, [history, queryObject, saveStoreInformationSuccess]);

  useEffect(() => {
    if (!saveStoreInformationSuccess) return;
    setLogoImageFile(null);
    setCoverImageFile(null);
    setProfileImageFile(null);
    setFaviconFile(null);
    setHasUnsavedChanges(false);
  }, [saveStoreInformationSuccess]);

  useEffect(() => {
    if (!coverShow || (coverShow && coverImage)) setMissingCover(false);
  }, [coverImage, coverShow]);

  useEffect(() => {
    if (!logoShape || (logoShape && logoImage)) setMissingLogo(false);
  }, [logoImage, logoShape]);

  useEffect(() => {
    if (colorSettingOption === '1' && primaryFontColor && secondaryFontColor)
      setMissingFontColor(false);
  }, [colorSettingOption, primaryFontColor, secondaryFontColor]);

  // Prevent accidental page close when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!storeInformation) {
      setHasUnsavedChanges(false);
      return;
    }

    // Check image file changes
    const imageFilesChanged =
      logoImageFile !== null ||
      coverImageFile !== null ||
      profileImageFile !== null ||
      faviconFile !== null;

    // Check logo and cover settings changes
    const logoAndCoverChanged =
      logoShape !== storeInformation.logoShapeType ||
      descriptionShow !== storeInformation.enabledDescription ||
      coverShow !== storeInformation.enabledCover;

    // Check template changes
    const templateChanged =
      hasCustomTemplateFeature &&
      templateType !==
        storeInformation.storefrontTemplateSetting?.storefrontTemplateType;

    // Check product shape changes
    const productShapeChanged =
      hasImageShapeFeature &&
      productShape !== storeInformation.imageSetting?.productImageShapeType;

    // Check font changes
    let fontChanged = false;
    if (hasCustomFontFeature && storeInformation.fontSetting) {
      const fontSetting = storeInformation.fontSetting;
      if (fontSetting.primaryFontType && fontSetting.secondaryFontType) {
        const currentFontOption = FONTS_OPTIONS[Number(fontsSelected)];
        fontChanged =
          currentFontOption.primaryFontTypeId !==
            fontSetting.primaryFontType.id ||
          currentFontOption.secondaryFontTypeId !==
            fontSetting.secondaryFontType.id;
      }
    }

    // Check color changes
    let colorChanged = false;
    if (storeInformation.colorSetting) {
      const colorSetting = storeInformation.colorSetting;

      if (colorSettingOption === '1') {
        colorChanged =
          primaryFontColor !== colorSetting.primaryFontColor ||
          secondaryFontColor !== colorSetting.secondaryFontColor;
      } else {
        const optionSelectedNumber = isNaN(Number(colorOptionSelected))
          ? 0
          : Number(colorOptionSelected);

        console.log({colorOptionSelected});

        const currentColorOption =
          DEFAULT_COLOR_OPTIONS[optionSelectedNumber] ??
          DEFAULT_COLOR_OPTIONS[0];
        colorChanged =
          currentColorOption.primaryBackgroundColor !==
            colorSetting.primaryBackgroundColor ||
          currentColorOption.secondaryBackgroundColor !==
            colorSetting.secondaryBackgroundColor;
      }
    }

    // Check shopping cart changes
    const shoppingCartChanged =
      shoppingCardEnabled === storeInformation.shoppingCartDisabled;

    // Check contact info changes
    const contactInfoChanged =
      contactInformationEnabled !==
      storeInformation.storeContactInformationEnabled;

    // Check navigation button changes
    const navigationButtonChanged =
      navigationButtonShow !== storeInformation.storeNavButtonEnabled ||
      navigationButtonText !== storeInformation.storeNavButtonText ||
      navigationButtonUrl !== storeInformation.storeNavButtonUrl;

    // Combine all changes
    const hasChanges =
      imageFilesChanged ||
      logoAndCoverChanged ||
      templateChanged ||
      productShapeChanged ||
      fontChanged ||
      colorChanged ||
      shoppingCartChanged ||
      contactInfoChanged ||
      navigationButtonChanged;

    setHasUnsavedChanges(hasChanges);
  }, [
    storeInformation,
    logoImageFile,
    coverImageFile,
    profileImageFile,
    faviconFile,
    logoShape,
    descriptionShow,
    coverShow,
    hasCustomTemplateFeature,
    templateType,
    hasImageShapeFeature,
    productShape,
    hasCustomFontFeature,
    fontsSelected,
    colorSettingOption,
    primaryFontColor,
    secondaryFontColor,
    colorOptionSelected,
    shoppingCardEnabled,
    contactInformationEnabled,
    navigationButtonShow,
    navigationButtonText,
    navigationButtonUrl,
  ]);

  const handleProductShape = (type: 'RECTANGLE' | 'SQUARE') => {
    setProductShape(type);
  };
  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{fontSize: '1.75rem'}} />
  );

  const onSubmitForm = values => {
    if (!isValidUrl) {
      scrollTop('smooth', undefined, 9999);
    } else if (logoShape && !logoImage) {
      setMissingLogo(true);
      scrollTop('smooth', undefined, 500);
    } else if (coverShow && !coverImage) {
      setMissingCover(true);
      scrollTop('smooth', undefined, mobile ? 900 : 700);
    } else if (
      colorSettingOption === '1' &&
      !(primaryFontColor && secondaryFontColor)
    ) {
      setMissingFontColor(true);
      scrollTop('smooth', undefined, mobile ? 1200 : 900);
    } else {
      const storeInformationRequest: SaveInformationRequest = {
        enabledCover: coverShow,
        enabledDescription: descriptionShow,
        profileInformation: {
          description: values.description,
          profilePhotoId:
            storeInformation &&
            storeInformation.profileInformation &&
            storeInformation.profileInformation.profilePhotoId &&
            !profileImage
              ? ''
              : undefined,
          videoSocialMediaTypeId:
            storeInformation &&
            storeInformation.profileInformation &&
            storeInformation.profileInformation.videoUrl === values.videoUrl
              ? undefined
              : videoSocialMediaTypeId,
          videoUrl:
            storeInformation &&
            storeInformation.profileInformation &&
            storeInformation.profileInformation.videoUrl === values.videoUrl
              ? undefined
              : values.videoUrl,
        },
        logoShapeType: !!logoShape ? logoShape : undefined,
        profileImageFile: profileImageFile ? profileImageFile : undefined,
        logoImageFile: logoImageFile ? logoImageFile : undefined,
        description: descriptionShow ? values.storeDescription : undefined,
        coverId: coverShow
          ? storeInformation && storeInformation.coverId && !coverImage
            ? ''
            : undefined
          : undefined,
        coverImageFile: coverShow ? coverImageFile : undefined,
        storeContactInformationEnabled: contactInformationEnabled,

        storeNavButtonEnabled: !!navigationButtonShow,
        storeNavButtonText: navigationButtonText || undefined,
        storeNavButtonUrl: navigationButtonUrl || undefined,
        shoppingCartDisabled: !shoppingCardEnabled,
      };
      if (hasCustomTemplateFeature && !!templateType) {
        storeInformationRequest.storefrontTemplateSetting = {
          enabled: true,
          storefrontTemplateType: templateType,
        };
      }
      if (hasImageShapeFeature && !!productShape) {
        storeInformationRequest.imageSetting = {
          enabled: true,
          productImageShapeType: productShape,
        };
      }

      if (faviconFile) {
        storeInformationRequest.faviconFile = faviconFile;
      }

      if (
        typeof colorOptionSelected !== 'undefined' ||
        colorSettingOption === '1'
      ) {
        const colorSetting = DEFAULT_COLOR_OPTIONS[Number(colorOptionSelected)];
        const enabledAdvancedConfiguration = colorSettingOption === '1';
        storeInformationRequest.colorSetting = {
          enabled: true,
          enabledAdvancedConfiguration,
          primaryBackgroundColor: enabledAdvancedConfiguration
            ? values.primaryBackgroundColor
            : colorSetting.primaryBackgroundColor,
          secondaryBackgroundColor: enabledAdvancedConfiguration
            ? values.secondaryBackgroundColor
            : colorSetting.secondaryBackgroundColor,
          primaryFontColor: enabledAdvancedConfiguration
            ? primaryFontColor
            : '#FFFFFF',
          secondaryFontColor: enabledAdvancedConfiguration
            ? secondaryFontColor
            : '#3A3A49',
        };
      }
      if (hasCustomFontFeature && typeof fontsSelected !== 'undefined') {
        const fontSetting = FONTS_OPTIONS[Number(fontsSelected)];
        storeInformationRequest.fontSetting = {
          enabled: true,
          primaryFontTypeId: fontSetting.primaryFontTypeId,
          secondaryFontTypeId: fontSetting.secondaryFontTypeId,
        };
      }

      saveStoreInformation({storeInformationRequest});
    }
  };

  const imageShapeDisabled = storeMetadata
    ? !storeMetadata?.featureRestrictions?.design?.IMAGE_SHAPE
    : false;

  if (upgradeModal)
    return (
      <div className={classes.upgradeContainer} onClick={closeUpgradeModal}>
        <ProplusUpgradeBanner
          title={bannerData.title}
          benefits={bannerData.benefits}
          content={bannerData.content}
          contentBg={bannerData.contentBg}
          onClick={closeUpgradeModal}
        />
        <SnackBar mode="success" message="Click para cerrar" />
      </div>
    );

  return (
    <>
      <CustomExitPrompt
        when={hasUnsavedChanges}
        title="¿Salir sin guardar?"
        description="Tienes cambios sin guardar que se perderán si sales ahora."
      />
      {isSaving && <LoadingSpinner />}
      <SnackBar
        mode="error"
        message={error ? 'No se pudo actualizar información de la tienda.' : ''}
      />
      <SnackBar
        mode="success"
        message={saveStoreInformationSuccess ? 'Guardado correctamente.' : ''}
      />
      <div className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={10} xl={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <>
                      <Typography className={classes.titleFormText}>
                        Agrega tu logo e imagen de portada
                      </Typography>
                      <Divider className={classes.divider} />
                    </>
                  }
                  startClosed={false}
                >
                  <CoverSection
                    storeInformation={storeInformation}
                    missingLogo={missingLogo}
                    missingCover={missingCover}
                    errors={errors}
                    register={register}
                    logoShape={logoShape}
                    setLogoShape={setLogoShape}
                    descriptionShow={descriptionShow}
                    setDescriptionShow={setDescriptionShow}
                    coverShow={coverShow}
                    setCoverShow={setCoverShow}
                    coverImage={coverImage}
                    setCoverImage={setCoverImage}
                    coverImageFile={coverImageFile}
                    setCoverImageFile={setCoverImageFile}
                    logoImage={logoImage}
                    setLogoImage={setLogoImage}
                    logoImageFile={logoImageFile}
                    setLogoImageFile={setLogoImageFile}
                    navigationButtonShow={navigationButtonShow}
                    setNavigationButtonShow={setNavigationButtonShow}
                    navigationButtonText={navigationButtonText}
                    setNavigationButtonText={setNavigationButtonText}
                    navigationButtonUrl={navigationButtonUrl}
                    setNavigationButtonUrl={setNavigationButtonUrl}
                  />
                </Collapsible>
              </Grid>
              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <>
                      <Typography className={classes.titleFormText}>
                        Agrega tu favicon
                        {!hasStoreFavicon && <PremiumFeatureBadge />}
                      </Typography>
                      <Divider className={classes.divider} />
                    </>
                  }
                  startClosed={!!queryObject.fromOnboarding}
                >
                  <FaviconSection
                    defaultFaviconUrl={getImageUrl(storeInformation?.faviconId)}
                    onChangeFavicon={file => {
                      setFaviconFile(file);
                    }}
                  />
                </Collapsible>
              </Grid>

              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <Typography className={classes.titleFormText}>
                      Fondo de la tienda
                    </Typography>
                  }
                  startClosed={!!queryObject.fromOnboarding}
                >
                  {storeInformation ? (
                    <BackgroundSection
                      defaultBackgroundColor={storeInformation.backgroundColor}
                      defaultBackgroundEnabled={
                        storeInformation.backgroundEnabled
                      }
                      defaultBackgroundType={storeInformation.backgroundType}
                      defaultDesktopPhotoImage={
                        storeInformation.backgroundPhotoDesktopId
                      }
                      defaultMobilePhotoImage={
                        storeInformation.backgroundPhotoMobileId
                      }
                      defaultPatternImage={storeInformation.backgroundPatternId}
                    />
                  ) : (
                    <div className={classes.onboardingMessage}>
                      <Typography className={classes.onboardingMessageText}>
                        Para personalizar el fondo de tu tienda, primero debes
                        completar los pasos iniciales de configuración.
                      </Typography>
                      <CustomButton
                        onClick={() => history.push('/home')}
                        variant="contained"
                        color="primary"
                      >
                        Ir a configuración inicial
                      </CustomButton>
                    </div>
                  )}
                </Collapsible>
              </Grid>

              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <Typography className={classes.titleFormText}>
                      Carrito de compras{' '}
                      <Tag
                        flat
                        label={shoppingCardEnabled ? 'Activado' : 'Desactivado'}
                        type={shoppingCardEnabled ? 'success' : 'danger'}
                      />
                    </Typography>
                  }
                >
                  <label className={classes.rowEnableSetting}>
                    <Typography className={classes.helperText}>
                      {shoppingCardEnabled ? 'Desactivar ' : 'Activar '}
                      el carrito de compras
                    </Typography>
                    <IosSwitch
                      name="cover"
                      checked={shoppingCardEnabled}
                      onChange={e => {
                        setShoppingCardEnabled(e.target.checked);
                      }}
                    />
                  </label>
                </Collapsible>
              </Grid>

              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <div className={classes.twoLinesTitle}>
                      <div className={classes.rowTitle}>
                        <Typography className={classes.titleFormText}>
                          Elige la plantilla de tu preferencia:
                        </Typography>

                        {!hasCustomTemplateFeature && <PremiumFeatureBadge />}
                      </div>
                    </div>
                  }
                  startClosed={!!queryObject.fromOnboarding}
                >
                  <TemplateSection
                    templateType={templateType}
                    setTemplateType={setTemplateType}
                  />
                </Collapsible>
              </Grid>

              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <Typography className={classes.titleFormText}>
                      Colores de tienda
                    </Typography>
                  }
                  startClosed={!!queryObject.fromOnboarding}
                >
                  <ColorsSection
                    missingFontColor={missingFontColor}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    register={register}
                    colorSettingOption={colorSettingOption}
                    setColorSettingOption={setColorSettingOption}
                    colorOptionSelected={colorOptionSelected}
                    setColorOptionSelected={setColorOptionSelected}
                    primaryFontColor={primaryFontColor}
                    setPrimaryFontColor={setPrimaryFontColor}
                    secondaryFontColor={secondaryFontColor}
                    setSecondaryFontColor={setSecondaryFontColor}
                  />
                </Collapsible>
              </Grid>
              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  title={
                    <div className={classes.twoLinesTitle}>
                      <div className={classes.rowTitle}>
                        <Typography className={classes.titleFormText}>
                          Tipografía
                        </Typography>
                        {!hasCustomFontFeature && <PremiumFeatureBadge />}
                      </div>

                      <Typography className={classes.subtitleFormText}>
                        Elegir combinación tipográfica de títulos y texto de
                        párrafos
                      </Typography>
                    </div>
                  }
                  startClosed
                >
                  <FontsSection
                    fontsSelected={fontsSelected}
                    setFontsSelected={setFontsSelected}
                  />
                </Collapsible>
              </Grid>
              <Grid item xs={12}>
                <Collapsible
                  startClosed
                  className={classes.collapsible}
                  title={
                    <div className={classes.twoLinesTitle}>
                      <div className={classes.rowTitle}>
                        <Typography className={classes.titleFormText}>
                          Forma de fotos de {storeElementTypeName}
                        </Typography>
                        {!hasImageShapeFeature && <PremiumFeatureBadge />}
                      </div>
                      <Typography className={classes.subtitleFormText}>
                        Elegir la forma de las fotos de los
                        {storeElementTypeName}s que que aparecerán en tu web.
                      </Typography>
                    </div>
                  }
                >
                  <Grid container spacing={1} style={{position: 'relative'}}>
                    {!hasImageShapeFeature && (
                      <RestrictionModal featureLabel="Forma de fotos" />
                    )}
                    {businessModel === 'PRODUCT' && (
                      <Grid item xs={12} md={6}>
                        <CustomButton
                          onClick={() => handleProductShape('RECTANGLE')}
                          className={classes.buttonSquare}
                          disabled={imageShapeDisabled}
                        >
                          <div className={classes.skeletonSquare}>
                            <div className={classes.containerCheckedBox}>
                              {productShape === 'RECTANGLE'
                                ? checkedCheckbox
                                : uncheckedCheckbox}
                            </div>
                            <img src={verticalShape} alt="vertical shape" />
                            <span>1080 x 1440</span>
                          </div>
                        </CustomButton>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <CustomButton
                        onClick={() => handleProductShape('SQUARE')}
                        className={classes.buttonSquare}
                        disabled={imageShapeDisabled}
                      >
                        <div className={classes.skeletonSquare}>
                          <div className={classes.containerCheckedBox}>
                            {productShape === 'SQUARE'
                              ? checkedCheckbox
                              : uncheckedCheckbox}
                          </div>
                          <img src={horizontalShape} alt="horizontal shape" />
                          <span>1080 x 1080</span>
                        </div>
                      </CustomButton>
                    </Grid>
                  </Grid>
                </Collapsible>
              </Grid>
              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  startClosed
                  title={
                    <>
                      <Typography className={classes.titleFormText}>
                        Agrega sección de Conócenos
                      </Typography>
                      <Divider className={classes.divider} />
                    </>
                  }
                >
                  <AboutUsSection
                    storeInformation={storeInformation}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    profileImage={profileImage}
                    setProfileImage={setProfileImage}
                    setProfileImageFile={setProfileImageFile}
                    isValidUrl={isValidUrl}
                    setIsValidUrl={setIsValidUrl}
                    setVideoSocialMediaTypeId={setVideoSocialMediaTypeId}
                  />
                </Collapsible>
              </Grid>
              <Grid item xs={12}>
                <Collapsible
                  className={classes.collapsible}
                  startClosed
                  title={
                    <>
                      <Typography className={classes.titleFormText}>
                        Mostrar Información de contacto
                      </Typography>
                      <Divider className={classes.divider} />
                    </>
                  }
                >
                  <div className={classes.container}>
                    <Typography className={classes.descriptionText}>
                      Elije si deseas que tus clientes se comuniquen contigo por
                      WhatsApp o por correo electrónico.
                      <br />
                      <br />
                      El método de pago{' '}
                      <span className={classes.descriptionBold}>
                        "Pedir por WhatsApp"
                      </span>{' '}
                      no se verá afectado.
                    </Typography>

                    <IosSwitch
                      checked={contactInformationEnabled}
                      onChange={e =>
                        setContactInformationEnabled(e.target.checked)
                      }
                    />
                  </div>
                </Collapsible>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      {!upgradeModal && (
        <>
          {!hasCustomizeStoreFeatureDisabled && (
            <Hidden xsDown>
              <div
                className={classNames(classes.bottomBar, {
                  [classes.bottomBarWithChanges]: hasUnsavedChanges,
                })}
              >
                <div className={classes.bottomBarContent}>
                  <CustomButton
                    variant="contained"
                    className={classes.bottomBarButton}
                    onClick={handleSubmit(onSubmitForm)}
                    disabled={isSaving}
                  >
                    Guardar cambios
                  </CustomButton>
                  {hasUnsavedChanges && (
                    <div className={classes.unsavedChangesIndicator}>
                      <div className={classes.unsavedChangesDot} />
                      <Typography className={classes.unsavedChangesText}>
                        Cambios sin guardar
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </Hidden>
          )}

          {!hasCustomizeStoreFeatureDisabled && (
            <Hidden smUp>
              <CustomButton
                variant="contained"
                className={classes.mobileBottomButton}
                onClick={handleSubmit(onSubmitForm)}
                disabled={isSaving}
              >
                Guardar
              </CustomButton>
            </Hidden>
          )}
        </>
      )}
    </>
  );
}

Customize.propTypes = {
  store: PropTypes.object,
  storeInformation: PropTypes.object,
  saveStoreInformation: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  saveStoreInformationSuccess: PropTypes.bool,
  error: PropTypes.string,
};
